import app from 'flarum/forum/app';
import Page from 'flarum/common/components/Page';
import extractText from 'flarum/common/utils/extractText';

import n8nMark from '../utils/n8nMark';
import { N8N_TAG_SLUG, seesN8nTag } from '../utils/routeState';

/**
 * `/recursos` en el SPA — el gemelo cliente de `Umbral\Comunidad\Content\Recursos`
 * (T12 · objetivo 8; rehecho en V13 contra el mockup D9/M7 y el Spec §12).
 *
 * EL CATALOGO NO SE DUPLICA AQUI. Llega en el payload del foro
 * (`Api\AddResourceLinks` -> `umbralResourceBlocks`), que lo lee del MISMO `ResourceLinks.php`
 * que usa el Blade. Escribir la lista otra vez en TypeScript la dejaria desincronizada al
 * primer cambio: es el fallo que §20.3 documento para las paginas del pie y que alli se evito
 * descubriendo las secciones en vez de listarlas.
 *
 * Tampoco se re-agrupa ni se re-ordena: el payload trae los BLOQUES ya formados, con su CTA y
 * su linea de resto. Lo unico que este fichero decide es como se pinta.
 *
 * Solo los TEXTOS salen del locale, con el mismo esquema de claves que usa PHP
 * (`item_<key>_title` / `item_<key>_body`, `block_<tier>_title` / `block_<tier>_access`), asi
 * que servidor y SPA dicen lo mismo por construccion.
 */
type ResourceItem = { key: string; tier: string; icon: string | null; url: string | null; route: string | null };
type ResourceBlock = { tier: string; cta: string | null; more: number; items: ResourceItem[] };
type ForumPayload = { data?: { attributes?: { umbralResourceBlocks?: unknown } } };

const PROGRAMMING_VISUAL_KEY = 'programacion_visual';
// Tickets last five minutes server-side. Refresh with a full minute of margin so a
// member who keeps this SPA page open never receives an already-expired link.
const N8N_TICKET_REFRESH_MS = 4 * 60 * 1000;
const N8N_TICKET_RETRY_MS = 30 * 1000;

export default class RecursosPage extends Page {
  private resourceBlocks: ResourceBlock[] | null = null;
  private ticketRefreshTimer: number | null = null;
  private ticketRefreshGeneration = 0;
  private initialN8nTicketRefreshPending = false;

  oninit(vnode: any) {
    super.oninit(vnode);

    app.setTitle(extractText(this.t('meta_title')));
    app.setTitleCount(0);

    this.resourceBlocks = this.payloadBlocks();

    // The forum bootstrap is intentionally long-lived, while this bearer expires in
    // minutes. Entering /recursos (and then every four minutes while it remains open)
    // asks the authenticated, same-origin ForumSerializer for a freshly minted href.
    if (this.hasSignedN8nLink(this.resourceBlocks)) {
      // A bootstrap can be older than the five-minute bearer. Keep only this card inert
      // until the same-origin refresh proves that its href was minted for this page entry.
      this.initialN8nTicketRefreshPending = true;
      this.refreshN8nTicket();
    }
  }

  onremove() {
    this.ticketRefreshGeneration += 1;
    this.clearTicketRefreshTimer();
  }

  view() {
    return (
      <div className="CmRes-container container">
        <div className="CmRes">
          <div className="CmRes-hero">
            <h1 className="CmRes-title">{this.t('title')}</h1>
            <p className="CmRes-intro">{this.t('intro')}</p>
          </div>

          <div className="CmRes-blocks">{this.blocks().map((block) => this.block(block))}</div>

          {/* Cierre: oscuro en los DOS temas (Spec §12 · reglas de diseño). */}
          <div className="CmRes-close">
            <div className="CmRes-closeText">
              <h3 className="CmRes-closeTitle">{this.t('close_title')}</h3>
              <p className="CmRes-closeBody">{this.t('close_body')}</p>
            </div>
            <a
              className="CmRes-closeCta Button Button--primary"
              href={this.catalogUrl()}
              target="_blank"
              rel="noopener"
            >
              {this.t('close_cta')}
            </a>
          </div>
        </div>
      </div>
    );
  }

  block(block: ResourceBlock) {
    // Un bloque sin enlaces no se pinta: un titulo con una lista vacia debajo es exactamente el
    // «feed vacio inutil» que esta pagina viene a sustituir.
    if (!block.items.length) return null;

    // El CTA de n8n es una ruta DEL FORO y se resuelve aqui, no en el payload: `app.route`
    // conoce el basePath de esta instalacion y el servidor no tiene por que adivinarlo.
    // El de planes sale del foro y por eso es el unico con `target="_blank"`.
    const external = block.cta === 'plans';
    const ctaUrl = external ? this.plansUrl() : this.routeUrl(block.cta);

    return (
      <section className="CmRes-block" data-cm-tier={block.tier}>
        <div className="CmRes-blockHead">
          <h2 className="CmRes-blockTitle">{this.t('block_' + block.tier + '_title')}</h2>
          {/* El subtítulo ES el acceso (Spec §12): por eso no hay chips por ítem. */}
          <span className="CmRes-blockAccess">{this.t('block_' + block.tier + '_access')}</span>
        </div>

        <div className="CmRes-list">
          {block.items.map((item) => this.link(item))}

          {block.more ? (
            <p className="CmRes-more">
              {app.translator.trans('umbral-comunidad.forum.recursos.more_line', { count: block.more })}
            </p>
          ) : null}
        </div>

        {/* El CTA va DESPUES de la lista en el DOM porque en movil ese es su sitio: full-width
            bajo los enlaces, no en el encabezado (Spec §12 · reglas de diseño). En escritorio
            la rejilla de `.CmRes-block` lo sube a la fila del titulo; asi hay UN solo CTA en el
            HTML y no dos escondiendose por media query, que es como se acaba anunciando el
            mismo boton dos veces a un lector de pantalla. */}
        {block.cta ? (
          <a
            className="CmRes-blockCta"
            href={ctaUrl}
            target={external ? '_blank' : undefined}
            rel={external ? 'noopener' : undefined}
          >
            {this.t('cta_' + block.cta)}
          </a>
        ) : null}
      </section>
    );
  }

  link(item: ResourceItem) {
    // `route` gana sobre `url`, y sólo uno de los dos viene informado. Un ítem con ruta se
    // queda DENTRO del foro, así que ni abre pestaña ni lleva el `↗`. Hoy NINGUNA ficha usa
    // ruta —desde PKG-FORO-UX-HOME-RECURSOS-INVITAR todas abren su recurso en la app y del
    // acceso habla el CTA del bloque— pero el mecanismo se queda para el catálogo futuro.
    const external = !item.route;
    const href = external ? item.url || '' : this.routeUrl(item.route);
    const waitingForFreshTicket = item.key === PROGRAMMING_VISUAL_KEY && this.initialN8nTicketRefreshPending;

    return (
      <a
        className={'CmRes-link' + (waitingForFreshTicket ? ' CmRes-link--refreshing' : '')}
        href={waitingForFreshTicket ? undefined : href}
        target={external && !waitingForFreshTicket ? '_blank' : undefined}
        rel={external && !waitingForFreshTicket ? 'noopener' : undefined}
        aria-disabled={waitingForFreshTicket ? 'true' : undefined}
        aria-busy={waitingForFreshTicket ? 'true' : undefined}
        data-cm-ticket-refreshing={waitingForFreshTicket ? 'true' : undefined}
        onclick={waitingForFreshTicket ? (event: Event) => event.preventDefault() : undefined}
      >
        {/* `icon === null` es el unico caso del catalogo y significa «pinta la marca n8n». */}
        <span className="CmRes-linkIcon">
          {item.icon ? <i className={item.icon} aria-hidden="true" /> : n8nMark('cm-n8n-mark cm-n8n-mark--inline')}
        </span>
        <span className="CmRes-linkText">
          <b className="CmRes-linkTitle">{this.t('item_' + item.key + '_title')}</b>
          <span className="CmRes-linkBody">{this.t('item_' + item.key + '_body')}</span>
        </span>
        {/* La señal «esto te saca del foro» del Spec §12, como icono y no como carácter: U+2197
            no tiene glifo en todas las plataformas y esta flecha no puede faltar. Lo interno
            lleva flecha recta: «sigue por aquí». */}
        <i className={'CmRes-linkGo fas ' + (external ? 'fa-external-link-alt' : 'fa-arrow-right')} aria-hidden="true" />
      </a>
    );
  }

  /**
   * Ruta del foro a partir de la etiqueta que trae el payload.
   *
   * El servidor manda una ETIQUETA (`request` o `group`) y no una URL a medias: sólo el cliente
   * conoce el basePath de esta instalación, y concatenarlo en PHP es el bug que T07 documentó
   * con `app.route()` en un initializer.
   *
   * CUÁL DE LAS DOS ETIQUETAS LLEGA NO SE DECIDE AQUÍ. La elige el servidor con el actor del
   * serializador (`Api\AddResourceLinks` → `ResourceLinks::effectiveRoute`), que es también quien
   * se la da al Blade. Si esta pantalla volviera a decidirlo por su cuenta, el HTML servido y el
   * SPA podrían mandar a la misma persona a sitios distintos — y esa divergencia es justo lo que
   * el catálogo compartido existe para impedir.
   *
   * Se resuelve contra un MAPA y no con un ternario, para que añadir una etiqueta al catálogo
   * PHP sin añadirla aquí sea visible: lo desconocido cae a la página del grupo —una página que
   * existe y explica de qué va todo esto— en vez de producir `href="undefined"`, que es un
   * enlace que parece bueno y no lleva a ninguna parte.
   */
  routeUrl(route: string | null): string {
    const routes: Record<string, string> = {
      request: app.route('umbral-comunidad.solicitud'),
      group: this.groupUrl(),
    };

    return (route && routes[route]) || app.route('umbral-comunidad.n8nChile');
  }

  /**
   * A dónde va quien YA está dentro: la categoría si de verdad la ve, y si no su página.
   *
   * Pertenecer al grupo y ver la etiqueta son cosas distintas —el permiso `tagN.viewForum` se
   * puede quedar sin poner, y en producción se quedó (runbook §T06-OPS)—, así que sin esta
   * segunda comprobación el arreglo cambiaría un rodeo por un 404. Misma guarda que
   * `N8nChilePage` y `SolicitudPage`.
   */
  groupUrl(): string {
    return seesN8nTag() ? app.route('tag', { tags: N8N_TAG_SLUG }) : app.route('umbral-comunidad.n8nChile');
  }

  blocks(): ResourceBlock[] {
    return this.resourceBlocks || this.payloadBlocks();
  }

  private payloadBlocks(): ResourceBlock[] {
    const raw = app.forum.attribute<ResourceBlock[]>('umbralResourceBlocks');

    return Array.isArray(raw) ? raw : [];
  }

  private refreshN8nTicket(): void {
    this.clearTicketRefreshTimer();
    const generation = ++this.ticketRefreshGeneration;

    window
      .fetch(app.forum.attribute('apiUrl'), {
        method: 'GET',
        cache: 'no-store',
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      })
      .then((response) => {
        if (response.status !== 200) throw new Error('resource ticket refresh failed');

        return response.json() as Promise<ForumPayload>;
      })
      .then(
        (payload) => {
          if (generation !== this.ticketRefreshGeneration) return;

          const raw = payload && payload.data && payload.data.attributes ? payload.data.attributes.umbralResourceBlocks : null;

          if (!Array.isArray(raw)) {
            this.scheduleN8nTicketRefresh(N8N_TICKET_RETRY_MS);
            return;
          }

          this.resourceBlocks = raw as ResourceBlock[];
          this.initialN8nTicketRefreshPending = false;
          app.forum.pushAttributes({ umbralResourceBlocks: this.resourceBlocks });
          m.redraw();

          if (this.hasSignedN8nLink(this.resourceBlocks)) {
            this.scheduleN8nTicketRefresh(N8N_TICKET_REFRESH_MS);
          }
        },
        () => {
          if (generation === this.ticketRefreshGeneration) {
            this.scheduleN8nTicketRefresh(N8N_TICKET_RETRY_MS);
          }
        }
      );
  }

  private scheduleN8nTicketRefresh(delay: number): void {
    this.ticketRefreshTimer = window.setTimeout(() => this.refreshN8nTicket(), delay);
  }

  private clearTicketRefreshTimer(): void {
    if (this.ticketRefreshTimer !== null) {
      window.clearTimeout(this.ticketRefreshTimer);
      this.ticketRefreshTimer = null;
    }
  }

  private hasSignedN8nLink(blocks: ResourceBlock[]): boolean {
    const item = blocks.flatMap((block) => block.items).find((candidate) => candidate.key === PROGRAMMING_VISUAL_KEY);

    if (!item || !item.url) return false;

    try {
      return new URL(item.url).searchParams.has('n8n');
    } catch (_error) {
      return false;
    }
  }

  /** Planes y precios de la app — CTA del bloque de pago. */
  plansUrl(): string {
    return app.forum.attribute<string>('umbralResourcePlansUrl') || 'https://beta.umbralbim.io/precios';
  }

  /**
   * Catálogo completo en la app — CTA de cierre.
   *
   * NO es el apex: `umbralbim.io` está en HOLD y devuelve la landing de espera a quien no esté
   * en la allowlist interna, así que el botón «Ver el catálogo» no enseñaba catálogo alguno.
   * Los dos por defecto de este fichero existen sólo para el caso en que el payload llegue sin
   * el atributo (extensión a medio desplegar); la fuente es `ResourceLinks`.
   */
  catalogUrl(): string {
    return app.forum.attribute<string>('umbralResourceCatalogUrl') || 'https://beta.umbralbim.io/recursos';
  }

  t(key: string) {
    return app.translator.trans('umbral-comunidad.forum.recursos.' + key);
  }
}
